/**
 * myDayBriefing.js
 *
 * Pure rules of the daily "Mai napom" (My day) briefing: storage keys, snooze choices and the auto-open decision.
 * Mirrors the admin panel's `resolveAutoOpen` precedence:
 * not-ready -> skipped -> snoozed (delay) / snooze-due -> next-launch -> first-today -> seen.
 * No React / storage / network imports — Jest-safe. Persistence lives in {@link module:helpers/myDayStore}.
 *
 * @module helpers/myDayBriefing
 */

/** Snooze choices in minutes (same set as the admin panel). */
export const SNOOZE_MINUTES = Object.freeze([15, 60, 180]);

/** i18n key per snooze choice. */
export const SNOOZE_LABEL_KEYS = Object.freeze({15: 'myday_snooze_15', 60: 'myday_snooze_60', 180: 'myday_snooze_180'});

/** A return from the background after at least this long counts as a "next launch". */
export const BACKGROUND_REOPEN_MS = 10 * 60 * 1000;

/** Routes on which the briefing never opens (login, lock, 2FA, onboarding / start). */
export const BRIEFING_BLOCKED_ROUTES = Object.freeze(['StartScreen', 'LoginScreen', 'ForgotPasswordScreen', 'TwoFactorScreen']);

/** Stable identifier of the user's pending local reminder. */
export const reminderIdentifier = (userId) => `myday:snooze:${userId}`;

/** @param {string} userId user id. @param {string} day local day key. @returns {string} "opened today" key. */
export const openedKey = (userId, day) => `myDay:opened:${userId}:${day}`;
/** @param {string} userId user id. @param {string} day local day key. @returns {string} "skip today" key. */
export const hideKey = (userId, day) => `myDay:hide:${userId}:${day}`;
/** @param {string} userId user id. @returns {string} pending snooze key (epoch ms). */
export const snoozeKey = (userId) => `myDay:snooze:${userId}`;
/** @param {string} userId user id. @returns {string} "next launch" flag key. */
export const nextLaunchKey = (userId) => `myDay:nextLaunch:${userId}`;

/**
 * Epoch ms a snooze of `minutes` ends at (only the preset choices are accepted).
 * @param {number} minutes minutes from SNOOZE_MINUTES.
 * @param {number} [now] reference epoch ms.
 * @returns {number|null} due time, or null for an unknown choice.
 */
export const snoozeDueAt = (minutes, now = Date.now()) => (
    SNOOZE_MINUTES.includes(Number(minutes)) ? now + Number(minutes) * 60 * 1000 : null
);

/**
 * Whether the current route allows the briefing (not login / lock / 2FA / start).
 * @param {string|undefined|null} routeName current route name; empty = navigation not ready.
 * @returns {boolean} true when the briefing may open here.
 */
export const isRouteAllowed = (routeName) => !!routeName && !BRIEFING_BLOCKED_ROUTES.includes(routeName);

/**
 * Auto-open decision.
 * @param {object} s state.
 * @param {boolean} s.ready logged in, user + store resolved, navigation ready, route allowed.
 * @param {boolean} [s.optedOut] today's "Ne mutasd ma többet" opt-out of the Teendők screen.
 * @param {boolean} [s.hidden] "Kihagyás ma" pressed today.
 * @param {boolean} [s.opened] already shown today.
 * @param {number|null} [s.snoozeUntil] pending snooze (epoch ms).
 * @param {boolean} [s.nextLaunch] "Következő induláskor" pending.
 * @param {'cold'|'foreground'} [s.trigger] what is being evaluated.
 * @param {number} [s.backgroundMs] time spent in the background before this foreground.
 * @param {number} [s.now] epoch ms.
 * @returns {{open: boolean, reason: string, delayMs?: number}} `reason`: not-ready | skipped | snoozed | snooze-due |
 *   next-launch | first-today | seen. For `snoozed`, `delayMs` is when to re-evaluate.
 */
export const resolveAutoOpen = ({
    ready, optedOut = false, hidden = false, opened = false, snoozeUntil = null, nextLaunch = false,
    trigger = 'cold', backgroundMs = 0, now = Date.now(),
} = {}) => {
    if (!ready) {
        return {open: false, reason: 'not-ready'};
    }

    if (optedOut || hidden) {
        return {open: false, reason: 'skipped'};
    }

    const until = Number(snoozeUntil);

    if (Number.isFinite(until) && until > 0) {
        return until > now
            ? {open: false, reason: 'snoozed', delayMs: until - now}
            : {open: true, reason: 'snooze-due'};
    }

    if (nextLaunch && (trigger === 'cold' || backgroundMs >= BACKGROUND_REOPEN_MS)) {
        return {open: true, reason: 'next-launch'};
    }

    if (!opened) {
        return {open: true, reason: 'first-today'};
    }

    return {open: false, reason: 'seen'};
};

/* ------------------------- manual open (tiny pub/sub) ------------------------- */

const openListeners = new Set();

/**
 * Subscribe to manual open requests (e.g. the Teendők screen button). Used by the root-mounted briefing.
 * @param {() => void} listener callback.
 * @returns {() => void} unsubscribe.
 */
export const subscribeMyDayOpen = (listener) => {
    openListeners.add(listener);

    return () => openListeners.delete(listener);
};

/**
 * Ask the root-mounted briefing to open now (no-op when it is not mounted).
 * @returns {boolean} true when a listener received the request.
 */
export const requestMyDayOpen = () => {
    openListeners.forEach((fn) => fn());

    return openListeners.size > 0;
};
